
import React, { useEffect, useRef, useState } from "react";
import { apiUrl } from "../../config";
import { FullDetails } from "../../design-system/components/FullDetails";
import { useAuth } from "../../hooks/useAuth";

/**
 * FACEIT Data API: Players
 * This page calls your backend wrapper for GET /players/{player_id}.
 * What you get without fetching: player metadata such as player_id, nickname, avatar, country,
 * game skill levels, and other public profile information.
 *
 * (Reference: FACEIT Open Data API v4 — players endpoint is part of /data/v4.)
 */
export function PlayerById() {
  const { user } = useAuth();
  const [id, setId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const autoFetched = useRef(false);

  async function fetchPlayer() {
    setErr(null);
    setData(null);
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/faceit/players/${encodeURIComponent(id)}`));
      const json = await res.json();
      if (!res.ok || json?.error) setErr(json?.error || `HTTP ${res.status}`);
      else setData(json);
    } catch (e: any) {
      setErr(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const faceitId = user?.faceit?.playerId;
    if (!faceitId || autoFetched.current) return;
    autoFetched.current = true;
    setId(faceitId);
    void fetchPlayer();
  }, [user]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">FACEIT Player by ID</h2>
        <p className="text-sm text-muted">
          Returns a public <em>Player</em> document (e.g., <code>player_id</code>, <code>nickname</code>, <code>avatar</code>, <code>country</code>, skill levels).
        </p>
      </div>

      {/* Form */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="FACEIT player_id"
            className="flex-1 bg-bg border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#4FC3F7]"
          />
          <button
            type="button"
            onClick={fetchPlayer}
            disabled={!id || loading}
            className="px-3 py-2 rounded-md border border-border hover:border-[#4FC3F7] hover:text-[#4FC3F7] transition text-sm"
          >
            {loading ? "Loading…" : "Fetch"}
          </button>
        </div>

        {err && <pre className="text-xs text-red-400 overflow-x-auto">{err}</pre>}
      </div>

      {/* Result */}
      {data && (
        <FullDetails data={data} title="All fields" open />
      )}
    </div>
  );
}
